import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import {
  AutocompleteMultiSelect,
  AutocompleteSingleSelect,
  type AutocompleteSingleSelectOption,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  Label,
  Switch,
  Textarea,
  toast,
} from '@shigo-ui/ui';

import { toSupplyAppsOptions } from '../../referenceOptions';
import { showRequestError } from '../../../../../lib/requestErrorToast';
import { dashboardT } from '../../../../../lib/messages';
import { useBusyDialogGuard } from '../../../../../hooks/useBusyDialogGuard';
import {
  applyAppRequestEdit,
  appRequestInitialAppId,
  appRequestEditSchema,
  type AppRequestEditValue,
} from '../../appRequestFormSchema';
import {
  useSupplyAppsMutations,
  useSupplyAppsQuery,
  useSupplyAppPublisherSearch,
} from '../../hooks';
import {
  defaultSupplyAppsListFilter,
  SupplyInventoryAppStatus,
  type AppRequest,
} from '../../model';

export function AppRequestEditorDialog({
  requests,
  onClose,
  onSaved,
}: {
  requests: AppRequest[];
  onClose: () => void;
  onSaved?: () => void;
}) {
  'use no memo';
  const current = requests[0];
  const connectionLocked = requests.some((request) => Boolean(request.appId));
  const initialAppId = appRequestInitialAppId(requests);
  const [publisherIds, setPublisherIds] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState(() =>
    !connectionLocked && initialAppId ? (current?.bundleId?.trim() ?? '') : '',
  );
  const [publisherSearch, setPublisherSearch] = useState('');
  const publishers = useSupplyAppPublisherSearch(publisherSearch, !connectionLocked);
  const [selectedPublishers, setSelectedPublishers] = useState<AutocompleteSingleSelectOption[]>(
    [],
  );
  const publisherOptions = toSupplyAppsOptions(publishers.data?.content ?? []);
  for (const option of selectedPublishers) {
    if (!publisherOptions.some((item) => item.id === option.id)) publisherOptions.push(option);
  }
  const appsEnabled = !connectionLocked && Boolean(searchTerm.trim() || publisherIds.length);
  const [selectedOption, setSelectedOption] = useState(() => ({
    id: initialAppId,
    label: current?.appId
      ? (current.connectedAppName ?? current.appId)
      : (current?.suggestedAppName ?? current?.suggestedAppId ?? ''),
  }));
  const form = useForm<AppRequestEditValue>({
    resolver: zodResolver(appRequestEditSchema),
    defaultValues: {
      appId: initialAppId,
      blocked: requests.every((request) => request.blocked),
      note: requests.length === 1 ? (current?.note ?? '') : '',
    },
  });
  const apps = useSupplyAppsQuery(
    {
      ...defaultSupplyAppsListFilter,
      statuses: [SupplyInventoryAppStatus.Active],
      publisherIds,
      searchTerm,
      size: 50,
    },
    appsEnabled,
  );
  const appOptions: AutocompleteSingleSelectOption[] = (
    (appsEnabled ? apps.data?.content : []) ?? []
  ).map((app) => ({
    id: app.id,
    label: `${app.name} · ${app.appStoreId ?? app.bundleId ?? ''}`,
  }));
  if (selectedOption.id && !appOptions.some((option) => option.id === selectedOption.id)) {
    appOptions.push({ ...selectedOption, disabled: true });
  }
  const mutation = useSupplyAppsMutations().processRequests;
  const dialogGuard = useBusyDialogGuard(mutation.isPending, onClose);

  async function submit(value: AppRequestEditValue) {
    const updated = applyAppRequestEdit(requests, value, form.formState.dirtyFields);
    try {
      await mutation.mutateAsync(updated);
      toast.success(dashboardT('supplyApps.request.saved'));
      onSaved?.();
      onClose();
    } catch (error) {
      showRequestError(error, dashboardT('supplyApps.request.saveError'));
    }
  }

  return (
    <Dialog open onOpenChange={dialogGuard.onOpenChange}>
      <DialogContent
        className="max-w-2xl gap-0 p-0"
        onEscapeKeyDown={dialogGuard.onDismissInteraction}
        onPointerDownOutside={dialogGuard.onDismissInteraction}
      >
        <DialogHeader className="border-border border-b px-5 py-4">
          <DialogTitle className="text-[15px]">
            {dashboardT('supplyApps.request.editTitle')}
          </DialogTitle>
          <DialogDescription>{dashboardT('supplyApps.request.editDescription')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form noValidate onSubmit={(event) => void form.handleSubmit(submit)(event)}>
            <div className="max-h-[70vh] space-y-5 overflow-y-auto px-5 py-5">
              <div className="bg-muted/40 border-border grid gap-2 rounded-md border p-4 text-sm sm:grid-cols-2">
                <span>
                  <strong>
                    {dashboardT('supplyApps.request.selected', { count: requests.length })}
                  </strong>
                </span>
                <span>
                  <strong>{dashboardT('supplyApps.publisher')}:</strong>{' '}
                  {Array.from(
                    new Set(requests.map((request) => request.publisherName).filter(Boolean)),
                  ).join(', ') || '-'}
                </span>
                <span className="truncate">
                  <strong>{dashboardT('supplyApps.bundleId')}:</strong>{' '}
                  {Array.from(
                    new Set(requests.map((request) => request.bundleId).filter(Boolean)),
                  ).join(', ') || '-'}
                </span>
                <span>
                  <strong>{dashboardT('supplyApps.platform')}:</strong>{' '}
                  {Array.from(
                    new Set(requests.map((request) => request.platformName).filter(Boolean)),
                  ).join(', ') || '-'}
                </span>
              </div>
              <fieldset className="space-y-3">
                <legend className="mb-1.5 text-[13px] font-semibold">
                  {dashboardT('supplyApps.request.connectApp')}
                </legend>
                <AutocompleteMultiSelect
                  label={dashboardT('supplyApps.publishersFilter')}
                  placeholder={dashboardT('supplyApps.request.searchPublishers')}
                  options={publisherOptions}
                  disabled={connectionLocked}
                  value={publisherIds}
                  onValueChange={(ids) => {
                    setPublisherIds(ids);
                    const selectedIds = new Set(ids);
                    setSelectedPublishers(
                      publisherOptions.filter((option) => selectedIds.has(option.id)),
                    );
                    setSearchTerm('');
                  }}
                  onSearchTermChange={setPublisherSearch}
                  searchDebounceMs={300}
                  minSearchLength={1}
                  loading={publishers.isFetching}
                  emptyMessage={dashboardT('supplyApps.lookupEmpty')}
                />
                {publishers.isError ? (
                  <p role="alert" className="text-destructive text-xs">
                    {dashboardT('supplyApps.lookupError')}
                  </p>
                ) : null}
                <div className="space-y-1.5">
                  <Controller
                    control={form.control}
                    name="appId"
                    render={({ field }) => (
                      <AutocompleteSingleSelect
                        label={dashboardT('supplyApps.request.connectedAppField')}
                        value={field.value}
                        clearable={false}
                        disabled={connectionLocked}
                        onValueChange={(value) => {
                          field.onChange(value);
                          setSelectedOption(
                            appOptions.find((option) => option.id === value) ?? {
                              id: '',
                              label: '',
                            },
                          );
                        }}
                        options={appOptions}
                        onSearchTermChange={setSearchTerm}
                        searchDebounceMs={300}
                        maxResults={51}
                        minSearchLength={appsEnabled ? 0 : 1}
                        loading={apps.isFetching}
                        placeholder={dashboardT('supplyApps.request.typeToSearch')}
                        searchPlaceholder={dashboardT('supplyApps.search')}
                        emptyMessage={dashboardT('supplyApps.lookupEmpty')}
                      />
                    )}
                  />
                  {appsEnabled && apps.isError ? (
                    <div className="flex items-center gap-2">
                      <p role="alert" className="text-destructive text-xs">
                        {dashboardT('supplyApps.request.appsError')}
                      </p>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => void apps.refetch()}
                      >
                        {dashboardT('common.retry')}
                      </Button>
                    </div>
                  ) : null}
                </div>
              </fieldset>
              <Controller
                control={form.control}
                name="blocked"
                render={({ field }) => (
                  <div className="border-input flex items-center justify-between rounded-md border px-3 py-2">
                    <Label htmlFor="app-request-blocked">
                      {dashboardT('supplyApps.request.blocked')}
                    </Label>
                    <Switch
                      id="app-request-blocked"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </div>
                )}
              />
              <div className="space-y-1.5">
                <Label htmlFor="app-request-note" className="text-[13px] font-semibold">
                  {dashboardT('supplyApps.request.note')}
                </Label>
                <Textarea id="app-request-note" rows={3} {...form.register('note')} />
              </div>
            </div>
            <DialogFooter className="border-border border-t px-5 py-4">
              <Button
                type="button"
                variant="outline"
                disabled={mutation.isPending}
                onClick={dialogGuard.close}
              >
                {dashboardT('supplyApps.cancel')}
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {dashboardT(
                  mutation.isPending ? 'supplyApps.request.saving' : 'supplyApps.request.save',
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
